import {
  getMenus,
  saveMenuItem,
  saveMenuItemOrder,
  deleteMenuItem,
  saveMenu,
} from '@/api/menus';
import { Menu, MenuState, MenuItem } from '@/types/menus';
import { RootState } from '@/types/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import _ from 'lodash';

type MenuGetter = GetterTree<MenuState, RootState>;

const state: MenuState = {
  menus: [],
  selected: [],
};

const getters: MenuGetter = {
  menus: state => state.menus,
  selectedMenu: state => state.selected,
};

const actions: ActionTree<MenuState, RootState> = {
  async getMenus({ commit }): Promise<void> {
    commit('setMenus', await getMenus());
  },

  async getMenu({ commit }, id) {
    const index = state.menus.findIndex((menu: Menu) => menu.id === id);
    let newMenu: Menu = {
      name: '',
      title: '',
    };
    if (index >= 0) {
      newMenu = state.menus[index];
    }
    commit('setSelectedMenu', newMenu);
  },

  async saveMenu({ commit }, menu) {
    const updatedMenu = await saveMenu(menu);
    commit('setSelectedMenu', updatedMenu);
    commit('updateMenu', updatedMenu);
  },

  async saveMenuItem({ commit }, menuItem) {
    const item = await saveMenuItem(menuItem);
    if (_.isNull(item.parent_id)) {
      commit('updateMenuItem', item);
    }
  },

  async saveMenuItemOrder({ commit }, update) {
    await saveMenuItemOrder(update);
  },

  async deleteMenuItem({ commit }, menuItem) {
    await deleteMenuItem(menuItem);
    if (_.isNull(menuItem.parent_id)) {
      commit('removeMenuItem', menuItem);
    } else {
      commit('removeMenuChildItem', menuItem);
    }
  },
};

const mutations: MutationTree<MenuState> = {
  setMenus(menuState, menuList: Menu[]) {
    menuState.menus = menuList;
  },

  setSelectedMenu(menuState, menu: Menu[]) {
    menuState.selected = menu;
  },

  updateMenu(menuState, updatedMenu: Menu) {
    const menuIndex = state.menus.findIndex(
      (menu: Menu) => menu.id === updatedMenu.id,
    );
    if (menuIndex < 0) {
      menuState.menus.push(updatedMenu);
    } else {
      menuState.menus[menuIndex] = updatedMenu;
    }
  },

  updateMenuItem(menuState, menuItem: MenuItem) {
    const menuIndex = state.menus.findIndex(
      (menu: Menu) => menu.id === menuItem.menu_id,
    );
    const menuItemIndex = state.menus[menuIndex].items!.findIndex(
      (item: MenuItem) => item.id === menuItem.id,
    );
    if (menuItemIndex < 0) {
      menuState.menus[menuIndex].items!.push(menuItem);
    } else {
      menuState.menus[menuIndex].items![menuItemIndex] = menuItem;
    }
  },

  removeMenuItem(menuState, menuItem: MenuItem) {
    const menuIndex = state.menus.findIndex(
      (menu: Menu) => menu.id === menuItem.menu_id,
    );
    const menuItemIndex = state.menus[menuIndex].items!.findIndex(
      (item: MenuItem) => item.id === menuItem.id,
    );
    if (menuItemIndex >= 0) {
      menuState.menus[menuIndex].items!.splice(menuItemIndex, 1);
    }
  },

  removeMenuChildItem(menuState, menuItem: MenuItem) {
    const menuIndex = state.menus.findIndex(
      (menu: Menu) => menu.id === menuItem.menu_id,
    );
    const parentMenuItemIndex = state.menus[menuIndex].items!.findIndex(
      (item: MenuItem) => item.id === menuItem.parent_id,
    );
    const menuItemIndex = state.menus[menuIndex].items![
      parentMenuItemIndex
    ].children!.findIndex((item: MenuItem) => item.id === menuItem.id);
    if (menuItemIndex >= 0) {
      menuState.menus[menuIndex].items![parentMenuItemIndex].children!.splice(
        menuItemIndex,
        1,
      );
    }
  },
};

const menus: Module<MenuState, RootState> = {
  state,
  getters,
  actions,
  mutations,
  namespaced: true,
};

export default menus;
